import { RenderFlags } from "./RenderFlags";
import { logger } from "../../logger/Logger";
import { getGlobal, isMobileDevice } from "../../compat";
import { GroundFlags } from "../render/GroundFlags";
import { RENDER_BUDGET_PER_BATCH, RENDER_SOME_CPU_TIME_FRACTION, MAX_RENDER_BUDGET, MIN_BATCH_TIME_MS, USE_OUT_OF_CORE_TILE_MANAGER, USE_WBOIT } from "../globals";
import * as shadow from "../render/ShadowMap";
import { ProgressState } from "../../application/ProgressState";
import { renderBudgetManager } from "../../application/RenderBudgetManager";
import * as et from "../../application/EventTypes";

const ENABLE_DEBUG = getGlobal().ENABLE_DEBUG;
const ENABLE_DEBUG_RCS = getGlobal().ENABLE_DEBUG_RCS;

const NOTHING_DRAWN = 0;
const MODEL_DRAWN = 1;
const BACKGROUND_DRAWN = 2;
const OVERLAY_DRAWN = 4;
const REFLECTION_DRAWN = 8;
const ALL_DRAWN = 15;


// various types of rendering
export const RENDERMODE_FULL = 0;
export const RENDERMODE_PROGRESSIVE = 1;
export const RENDERMODE_SILENT = 2;

// command states
export const CMD_NORMAL_SEQUENCE = 0;
export const CMD_DO_AFTER = 1;
export const CMD_ALWAYS_DO = 2;

/**
 * Helper function to render main-target render layers.
 * @param {Object} viewer - The viewer instance
 * @param {Array} layers - Array of render layers to render
 */
function renderMainRenderLayers(viewer, layers) {
  const renderer = viewer.renderer();

  for (const layer of layers) {
    // Render scene if present
    if (layer.scene && layer.scene.children.length !== 0) {
      renderer.renderScenePart(layer.scene, true, true, true);
    }

    // Render models if present
    if (layer.models && layer.models.length > 0) {
      renderer.renderLayerModels(layer.models);
    }

    // Clear depth after rendering if requested
    if (layer.clearDepthAfter) {
      renderer.clearMainDepth();
    }
  }
}

function cmdBeginScene() {

  const viewer = this.viewer;
  const _modelQueue = viewer.modelQueue();

  if (this.signalProgressByRendering)
  this.viewer.signalProgress(0, ProgressState.RENDERING); //zero out the progress bar for when rendering begins

  if (this.renderType === RENDERMODE_PROGRESSIVE) {
    // Measure actual frame time between two consecutive initial frames. Used to correct consumed
    // render-budget feedback to what frames actually take once async GPU work is accounted for
    // (see applyBudgetFeedback).
    this.lastBeginFrameTimeStamp = this.highResTimeStamp;
  }

  // clear the color and depth targets
  const clear = this.getParam("BeginScene.clear");

  // We call beginScene with the empty THREE.Scene just to update camera and lights uniforms
  viewer.renderer().beginScene(viewer.emptyScene, viewer.camera, viewer.lightsOn ? viewer.lights : viewer.no_lights, clear);

  // During loading, geometry arrival triggers invalidate(false, true), skipping clearMainTargets() while the
  // transparent traversal restarts; without this clear the interleaved preview accumulates into stale targets.
  // Gated on !areAllModelsLoaded(): post-load a needsClear=false cycle is a silent re-render that reuses the still
  // valid accum (#reuseTransparentAccum), so clearing it there would strand the persistent blend source empty.
  // tickCount===0 marks the first tick of a new cycle; the needsClear path clears via clearMainTargets().
  if (USE_WBOIT && this.tickCount === 0 && !clear && !viewer.modelQueue().areAllModelsLoaded()) {
    viewer.glrenderer().clearWboitTargets();
  }

  if (clear) {

    // Render main-target layers with renderOrder < 0 (before main scene)
    const mainLayers = viewer.getRenderLayerManager().getMainPassRenderLayers();
    if (mainLayers.before.length > 0) {
      renderMainRenderLayers(viewer, mainLayers.before);
    }

    this.screenDrawn |= BACKGROUND_DRAWN;
    _modelQueue.onClear();
  }

  // Check if the camera changed, and if so, signal.
  if (this.getParam("BeginScene.signalCameraChanged")) {
    // Tells view cube to update, for example.
    viewer.api.dispatchEvent(viewer.cameraChangedEvent);
  }

  return false;
}

function cmdMainRender() {
  let viewer = this.viewer;
  let _modelQueue = viewer.modelQueue();

  //This is done in _tick() instead
  //viewer._updateLastRenderTime();

  if (!_modelQueue.isEmpty() && !_modelQueue.isDone()) {

    this.screenDrawn |= MODEL_DRAWN;

    // It's important that we only scale the budget during the normal phase, since the error would multiply over the phases
    _modelQueue.setRenderBudgetScalingEnabled(this.phase === RenderFlags.RENDER_NORMAL);

    // When using WBOIT, only progressive frames after the first should
    // redraw transparent frags, and only during the normal phase.
    _modelQueue.setTransparentsNeedRedraw(
      USE_WBOIT &&
      // Don't reset while drawing ghosting, etc.
      this.phase === RenderFlags.RENDER_NORMAL &&
      // Don't reset on first progressive frame.
      this.renderType === RENDERMODE_PROGRESSIVE &&
      this.tickCount > 0);

    //Render some geometry with the current render mode (highlighted, normal, or ghosted)
    const renderSomeArgs = [
    viewer.renderSomeCallback.bind(viewer),
    this.renderBudgetRemaining];

    if (USE_OUT_OF_CORE_TILE_MANAGER) {
      renderSomeArgs.push(this._frameEndDeadline);
    }
    this.renderBudgetRemaining = _modelQueue.renderSome(...renderSomeArgs);

    // WebGPU renderer requires flushing the command queue after each progressive frame.
    if (viewer.isWebGPU()) {
      viewer.glrenderer().flushCommandQueue();
      if (viewer.getSheetRenderer()) {
        viewer.getSheetRenderer().glRenderer.flushCommandQueue();
      }
    }

    // TODO - cmdMainRender gets used by a number of systems - what sort of progress should really happen here?
    if (this.signalProgressByRendering) {
      viewer.signalProgress(100.0 * _modelQueue.getRenderProgress(), ProgressState.RENDERING);
      if (ENABLE_DEBUG_RCS) {
        console.log("  %%% percent done " + 100 * _modelQueue.getRenderProgress());
      }
    }
  }
  // Halt rendering if halt frame is set and matches the current tick count. This freezes progressive rendering at
  // the Nth intermediate frame and is intended for inspecting image completeness/convergence at a specific frame.
  if (this.progressiveHaltFrame > 0 && this.tickCount >= this.progressiveHaltFrame - 1) {
    return false;
  }

  // Rebuild the HZB whenever something was drawn this tick, regardless of whether the model queue
  // is still in progress. Placing it here (outside the !isDone check) ensures the pyramid is
  // up-to-date on the final converged frame and on any silent re-render.
  if (viewer.isWebGPU() && this.screenDrawn & MODEL_DRAWN) {
    viewer.glrenderer().computeHierarchicalDepth();
  }

  // if there is time left, continue on (return false), else return true, which means "stop for now"
  return !_modelQueue.isDone();
}

// render sectioning, if any, and any additional buffers needed, such as ID.
function cmdSceneAfterRender() {

  const viewer = this.viewer;

  this.phase = RenderFlags.RENDER_FINISHED;

  // Render main-target layers with renderOrder > 0 (after main scene)
  const mainLayers = viewer.getRenderLayerManager().getMainPassRenderLayers();
  if (mainLayers.after.length > 0) {
    renderMainRenderLayers(viewer, mainLayers.after);
  }

  // TODO: bad, renderScenePart does not return the amount of time used to render. It should, so we know the remaining budget.
  // TODO: to be honest, we should actually do a performance.now() at the beginning of any command list set, and
  // use *that* to track the time truly remaining. highResTimeStamp that is passed in is not trustworthy. But there's also the
  // "average batch time" that gets set, to minimize flicker. A creaky system that works, mostly.
  return false;
}

function cmdRenderOverlays() {

  const viewer = this.viewer;
  const _modelQueue = viewer.modelQueue();

  // Render selection highlight / pivot / HUD overlays and other overlay geometry
  // This is stuff that goes into the separate overlay render buffer. It does rely on the z-buffer being properly populated,
  // so is normally rendered after the beauty pass (or highlighting pass) is performed. As such, we need to rerender it on
  // every progressive pass.

  // If there was an overlay dirty (i.e., someone hovered over something in the middle of a progressive render), note that the
  // overlay must now be drawn from here on out.
  if (viewer.isOverlayDirty()) {
    // avoid having successive passes
    viewer.clearOverlayDirtyFlag();
    this.drawOverlay = true;
  }

  // draw if needed
  if (this.drawOverlay) {

    // If there is geometry, and we're done rendering it, OR we need to always render the overlay while moving, make the overlay
    if (!_modelQueue.isEmpty() && _modelQueue.isDone() || viewer.showOverlaysWhileMoving) {
      viewer.renderOverlays();
      this.screenDrawn |= OVERLAY_DRAWN;
    } else {
      // overlay update not needed (no geometry, or to be done only at end): clear once, and turn off drawing it since we need to clear only once.
      viewer.renderer().clearAllOverlays();
      this.drawOverlay = false;
    }
  }

  return false;
}

// if we're fading in the rollover highlight, we just need to compose the final frame.
// This command forces PostAndPresent to happen.
function cmdForcePresent() {
  this.screenDrawn |= ALL_DRAWN;
}

function cmdPostAndPresent() {

  const viewer = this.viewer;

  //Run post-processing and present to the front buffer
  if (this.screenDrawn && (
  // present if we're done rendering, or if progressive and this is a displayable frame
  this.phase === RenderFlags.RENDER_FINISHED || this.tickCount % this.frameDisplayRate === 0)) {

    // Skip AO if we clear the screen and nothing else was drawn, or if
    // it was disabled when we created the command list.
    let skipAO = !this.getParam("PostAndPresent.performAO") ||
    (this.screenDrawn & (BACKGROUND_DRAWN | MODEL_DRAWN)) === BACKGROUND_DRAWN;
    // present image
    viewer.renderer().composeFinalFrame(skipAO);
    this.screenHasChanged = true;
    // reset
    this.screenDrawn = NOTHING_DRAWN;

    viewer.api.dispatchEvent({ type: et.RENDER_PRESENTED_EVENT });
  }

  return false; // TODO - could actually measure time at this point
}

function cmdFinishAllRendering() {
  // in case some system is querying the phase
  this.phase = RenderFlags.RENDER_FINISHED;

  return false;
}

// Smooth navigation overrides
// TODO - I don't really like toggle SAO off and on during a single tick, it is a little
// costly (how much?), but it's the safest option.
function cmdSuppressAO() {

  const viewer = this.viewer;

  if (ENABLE_DEBUG) {
    if (viewer.renderer().getAOEnabled() === false) {
      // AO should be on and we should be suppressing it.
      logger.error("AO should be on at this point!");
    }
  }
  viewer.renderer().setAOEnabled(false);
  return false;
}
function cmdRestoreAO() {

  const viewer = this.viewer;

  if (ENABLE_DEBUG) {
    if (viewer.renderer().getAOEnabled() === true) {
      // AO should be off and we should be restoring it.
      logger.error("AO should be off at this point!");
    }
  }
  viewer.renderer().setAOEnabled(true);
  return false;
}

// Here's the system:
// If ground shadow is done - well, that's easy, just blit it before beauty pass
// If not done
//     If we are doing a full render
//     Render the whole shadow first (possibly tick limited), blit it to screen, then continue to beauty pass
//     Else we are doing progressive
//        if this is the first frame:
//           if the number of objects in the scene is low (10?),
//              render the drop shadow, figuring we can rendering the rest of the scene in a single frame
//           else
//              don't bother rendering anything in later ticks (we used to waste time rendering a few each tick)
//        if this is a later frame:
//           render just the beauty pass, until done. Don't bother with the shadow now, as it won't get used.
//        When we get to the end of progressive:
//           If needed, render the ground shadow until done. Once done, signal that a re-render is needed.
function cmdGenerateGroundShadow() {

  const viewer = this.viewer;
  let frameTimeRemaining;

  // three cases: full render, quick out for progressive, continue as possible for progressive.
  if (this.renderType === RENDERMODE_PROGRESSIVE) {
    if (this.getParam("GenerateGroundShadow.afterBeauty")) {
      // Rendering the ground shadow after all progressive rendering is done. Signal redraw if it finishes.
      if (USE_OUT_OF_CORE_TILE_MANAGER) {
        frameTimeRemaining = viewer.groundShadow().prepareGroundShadow(viewer.modelQueue(), 0, this.getRemainingFrameTime());
      } else {
        this.renderBudgetRemaining = viewer.groundShadow().prepareGroundShadow(viewer.modelQueue(), 0, this.renderBudgetRemaining);
      }
      // was this the call that rendered it fully?
      if (viewer.groundShadow().getStatus() === GroundFlags.GROUND_RENDERED) {
        // Do we need to rerender? This needs to happen if we're not using reflection insertion.
        // TODO: someday perhaps make ground shadows more "full featured" and merge behind, like ground reflections do?
        if (this.getParam("GenerateGroundShadow.signalRedraw")) {
          viewer.requestSilentRender();
          if (ENABLE_DEBUG_RCS) {
            console.log(" $$$$ SIGNAL FULL GROUND SHADOW REDRAW");
          }
          // don't need to continue, since we know we need to fully redraw on next tick
          return true; // TODO could signal abort rest of command stream, since we know we invalidate. It's just a bit inefficient otherwise.
        }
        // note for ground reflection, so it can decide on deferred silent rendering.
        this.groundShadowInPost = true;
      }
    } else {
      // If this is the first frame, try to render the drop shadow in a small amount of time.
      // Else, don't waste time on the drop shadow.
      if (this.tickCount === 0) {
        // render 10 objects TODO - expose 10 as some other number?
        //frameTimeRemaining =
        viewer.groundShadow().prepareGroundShadow(viewer.modelQueue(), 10, USE_OUT_OF_CORE_TILE_MANAGER ? 3 : undefined);
        // TODO or this way, which does possibly give flicker:
        //frameTimeRemaining = viewer.groundShadow().prepareGroundShadow(viewer.modelQueue(), this.getRemainingFrameTime(), this.INITIAL_GROUND_SHADOW);
        //const minRemaining = this.renderBudget * (1-this.INITIAL_GROUND_SHADOW);
        //if ( frameTimeRemaining < minRemaining ) {
        //     frameTimeRemaining = minRemaining;
        //}
      }
    }
  } else {
    // full render, just do it fully.
    if (USE_OUT_OF_CORE_TILE_MANAGER) {
      frameTimeRemaining = viewer.groundShadow().prepareGroundShadow(viewer.modelQueue(), 0, this.getRemainingFrameTime());
    } else {
      this.renderBudgetRemaining = viewer.groundShadow().prepareGroundShadow(viewer.modelQueue(), 0, this.renderBudgetRemaining);
    }
  }

  // rendering can continue if there's time left
  if (USE_OUT_OF_CORE_TILE_MANAGER) {
    return frameTimeRemaining < 0 && viewer.groundShadow().getStatus() === GroundFlags.GROUND_UNFINISHED;
  }
  return this.renderBudgetRemaining < 0 && viewer.groundShadow().getStatus() === GroundFlags.GROUND_UNFINISHED;
}

function cmdBlitGroundShadow() {
  const viewer = this.viewer;

  //Render the ground shadow after screen clear
  if (viewer.groundShadow().getStatus() !== GroundFlags.GROUND_UNFINISHED)
  viewer.renderGroundShadow();
  return false;
}

function cmdGenerateGroundReflection() {

  const viewer = this.viewer;
  const _groundReflection = viewer.groundReflection();
  let frameTimeRemaining;

  // three cases: full render, quick out for progressive, continue as possible for progressive.
  if (this.renderType === RENDERMODE_PROGRESSIVE) {
    // is this pass happening after the beauty pass is completed?
    if (this.getParam("GenerateGroundReflection.afterBeauty")) {
      // Rendering the ground reflection after all progressive rendering is done.
      if (USE_OUT_OF_CORE_TILE_MANAGER) {
        frameTimeRemaining = _groundReflection.prepareGroundReflection(viewer.groundShadow(), viewer, false, 0, this.getRemainingFrameTime());
      } else {
        this.renderBudgetRemaining = _groundReflection.prepareGroundReflection(viewer.groundShadow(), viewer, false, 0, this.renderBudgetRemaining);
      }
      // was this the call that rendered it fully?
      if (_groundReflection.getStatus() === GroundFlags.GROUND_RENDERED) {
        this.screenDrawn |= REFLECTION_DRAWN;
        // If we're done, we should also check to see if a silent render is needed for ground shadows.
        // If ground shadows were finished in these post-render passes (rare - only on animation or explode,
        // for complex scenes), transparent objects in the scene will not show the shadows properly through
        // their transparent objects, LMV-2508.
        // TODO - nicer still would be to see if the scene actually has any transparent objects. If not,
        // then we don't need this separate re-render.
        // TODO Also, note this isn't a perfect system: in practice you really want to have the ground reflection
        // entirely done before rendering atop it, so that what is seen through transparent objects is fully
        // folded in. However, this problem is much less obvious in the scenes tested - missing ground shadows
        // are more obvious.
        if (this.groundShadowInPost && viewer.matman().hasTransparentMaterial()) {
          viewer.requestDeferredSilentRender();
        }
      }
    } else {
      // If this is the first frame, try to render the reflection in a small amount of time.
      // Else, don't waste time on the reflection.
      if (this.tickCount === 0) {
        // render 10 objects TODO - expose 10 as some other number? Or use a budget? Or...?
        //frameTimeRemaining =
        _groundReflection.prepareGroundReflection(viewer.groundShadow(), viewer, true, 10, USE_OUT_OF_CORE_TILE_MANAGER ? 5 : undefined);
        // TODO or this way, which does possibly give flicker:
        //frameTimeRemaining = _groundReflection.prepareGroundReflection(viewer.groundShadow(), viewer, true, this.getRemainingFrameTime(), this.INITIAL_GROUND_SHADOW);
        //const minRemaining = this.renderBudget * (1-this.INITIAL_GROUND_SHADOW);
        //if ( frameTimeRemaining < minRemaining ) {
        //     frameTimeRemaining = minRemaining;
        //}
      }
    }
  } else {
    // full render, just do it fully.
    if (USE_OUT_OF_CORE_TILE_MANAGER) {
      frameTimeRemaining = _groundReflection.prepareGroundReflection(viewer.groundShadow(), viewer, false, 0, this.getRemainingFrameTime());
    } else {
      this.renderBudgetRemaining = _groundReflection.prepareGroundReflection(viewer.groundShadow(), viewer, false, 0, this.renderBudgetRemaining);
    }
  }

  // rendering can continue if there's time left, or if we actually finished display and should present, even though we're out of time.
  // TODO we could revise commands to be of "takes time" and "doesn't take time", so that we abort if and only if we're out of time
  // and hit a "takes time" command.
  if (USE_OUT_OF_CORE_TILE_MANAGER) {
    return frameTimeRemaining < 0 && _groundReflection.getStatus() === GroundFlags.GROUND_UNFINISHED;
  }
  return this.renderBudgetRemaining < 0 && _groundReflection.getStatus() === GroundFlags.GROUND_UNFINISHED;
}

/**
 * Progressive update of the shadow map:
 *
 *   a) For small models that can be rendered within a single frame, the shadow map will always be rendered first,
 *      so that shadows will not flicker on and off during animations, on scene changes, or when changing the light direction.
 *   b) For large models, seeing something is more important than shadows. Therefore, we render without shadows
 *      first and only do work on the shadow map if everything else is finished.
 *
 *  Whether we take a) or b) is determined on-the-fly: We use a) if we succeed updating the whole ShadowMap
 *  within a single frame time budget.
 */
function cmdUpdateShadowMap() {

  let viewer = this.viewer;
  let _shadowMaps = viewer.shadowMaps();

  // We are either starting an update of the shadow map, or are continuing to render it in this tick.

  // This section is always entered in the first frame if the shadow map is not available yet.
  if (_shadowMaps.state === shadow.SHADOWMAP_NEEDS_UPDATE) {

    // start shadow map update. This call may end in two ways:
    //  - In case a), the shadowmap could already be finished within the startUpdate() call. Therefore, the
    //    shadow map will already be available and will be used in this frame.
    //    In this case, there is nothing more to do and all subsequent calls to updateShadowMap will
    //    do nothing.
    //  - in case b), the shadow map is not available. In this case, we first wait until the rendering
    //    without shadows is finished. (see next section)
    this.renderBudgetRemaining = _shadowMaps.startUpdate(viewer.modelQueue(), this.renderBudgetRemaining, viewer.camera, viewer.getShadowLightDirection(), viewer.matman());

  } else if (_shadowMaps.state === shadow.SHADOWMAP_INCOMPLETE) {

    // continue shadow map update as long as we have budget / time left
    this.renderBudgetRemaining = _shadowMaps.continueUpdate(viewer.modelQueue(), this.renderBudgetRemaining, viewer.matman());

    // if we're done and this is a progressive render, then this shadow generation is happening at the end.
    // In such a case we need to re-render (similar to ground shadows and reflections).
    if (_shadowMaps.state === shadow.SHADOWMAP_VALID) {

      // TODO - may wish to make this a deferred silent render, so that reflection etc. is completed, then shadows come in later.
      viewer.requestSilentRender();
      if (ENABLE_DEBUG_RCS) {
        console.log(" $$$$ SIGNAL FULL SHADOW MAP REDRAW");
      }
      // don't need to continue, since we know we need to fully redraw on next tick
      return true; // TODO could signal abort rest of command stream, since we know we invalidate. It's just a bit inefficient otherwise.
    }
  }
  return this.renderBudgetRemaining < 0 && _shadowMaps.state !== shadow.SHADOWMAP_VALID;
}

function cmdResetShadowMap() {
  this.viewer.shadowMaps().state = shadow.SHADOWMAP_NEEDS_UPDATE;
}


function cmdBeginPhase() {
  // If nothing is highlighted just skip the highlighted phase
  this.phase = this.getParam("BeginPhase.phase");
  if (ENABLE_DEBUG_RCS) {
    console.log("     render phase is now " + this.phase);
  }

  // Start rendering the scene by resetting the rendering queue.
  // This sets up the view frustum intersector and begins scene iteration.
  const viewer = this.viewer;

  // WBOIT: when progressive rendering is off (single-tick full render), skip the interleaved transparent
  // preview to avoid opaque<->transparent pipeline thrashing in the combined pass. The dedicated correction
  // pass still runs, yielding a clean opaque phase then transparent phase within the one tick. Must be set
  // before reset(), which is where the combined heap is primed.
  viewer.modelQueue().setSkipInterleavedTransparents(USE_WBOIT && this.renderType === RENDERMODE_FULL);

  viewer.modelQueue().reset(viewer.camera, this.phase, this.getParam("BeginPhase.moved"), viewer.matman().getCutPlanes(), viewer.cutplanesHideInteriorEnabled());

  return false;
}


function cmdSignalProcessingDone() {
  //if (this.signalProgressByRendering)
  this.viewer.signalProgress(100.0, ProgressState.RENDERING);
}

function cmdSignalRedraw() {
  this.viewer.requestSilentRender();
  return false;
}
function cmdFinishedFullRender() {
  this.finishedFullRender = true;
  return false;
}

// The render command system is what actually does the render. The idea here is that each tick() checks if anything causes a new
// render. If so, then we make a new list of commands to perform, then start performing them. For a full render without interruptions,
// this is overkill - we could just lockstep execute them all. Where the command list comes into its own is that it can be continued.
// For progressive rendering we want
// Rather than pepper the rendering sequence of the code with lots of "if" statements that
export class RenderCommandSystem {

  constructor(viewerImpl)
  {
    this.viewer = viewerImpl;

    // Desired frame time in milliseconds. Target for the budget controller and CPU deadlines;
    // adjusted once actual frame times (including async GPU work, system load, etc.) are measured.
    // NOTE: TARGET_FRAME_TIME is doubled for mobile devices below.
    this.TARGET_FRAME_TIME = 1000 / 30;
    // Per-tick render budget limits. Units are batches (OOC) or milliseconds (legacy).
    this._syncRenderBudgetBounds();
    this.targetRenderBudget = USE_OUT_OF_CORE_TILE_MANAGER ?
    Math.max(RENDER_BUDGET_PER_BATCH, Math.round(this.TARGET_FRAME_TIME)) :
    this.TARGET_FRAME_TIME;

    // How many ticks pass in between updates. Make this half as many for mobile because the frame budget is doubled.
    // 1 means that we display every frame
    this.frameDisplayRate = 5;
    if (isMobileDevice()) {
      this.TARGET_FRAME_TIME *= 2; // GPUs are slower on mobile use a longer target frame time
      if (USE_OUT_OF_CORE_TILE_MANAGER) {
        this.targetRenderBudget = Math.max(RENDER_BUDGET_PER_BATCH, Math.round(this.targetRenderBudget / 2));
      } else {
        this.MAX_RENDER_BUDGET *= 2;
        this.targetRenderBudget /= 2;
      }
      this.frameDisplayRate /= 2; // since time per tick is doubled (in the long run), halve the number of ticks to give the same wall-clock delay interval
    }
    // How much time between checks on a full frame for any interrupt signal.
    this.interruptBudget = 1e10;

    this.highResTimeStamp = -1;
    // did something get rendered that would change the screen (almost always true when rendering occurs)?
    this.screenDrawn = NOTHING_DRAWN;
    // did a Present get done?
    this.screenHasChanged = false;
    // how many render-budget units are allocated for this tick (batches, or ms on the legacy path).
    this.renderBudget = 0;
    // how much render budget is already used up this tick?
    this._consumedRenderBudget = 0;
    /**
     * Consumed render budget of the current display frame's first (interactive) tick — the tick
     * whose work maps to the measured wall-clock frame time. Only tick 0 contributes; convergence
     * ticks (tickCount > 0) are excluded. Handed on to _consumedRenderBudgetDelayed by the next
     * applyBudgetFeedback call, or dropped with the baseline it belongs to
     * (see _dropProgressiveFrameSample).
     */
    this._consumedRenderBudgetPending = 0;
    /**
     * Consumed render budget of the frame sampled before the current one. This is what gets fed to
     * RenderBudgetManager: the GPU lags the CPU by ~one frame, so the wall-clock delta measured
     * for the just-displayed frame corresponds to work submitted one frame earlier.
     * Starts undefined; the first applyBudgetFeedback call passes that through and
     * updateRenderBudget intentionally skips the budget update.
     */
    this._consumedRenderBudgetDelayed = undefined;
    // Fraction of the total render budget that has been allocated to this viewer instance
    this.renderBudgetFraction = 1;
    // how much render budget is left during this tick() (batches, or ms on the legacy path).
    this.renderBudgetRemaining = 0;
    // CPU wall-clock deadline for this tick (performance.now() + allocated frame slice).
    this._frameEndDeadline = Infinity;
    // what type of render is happening currently
    this.phase = RenderFlags.RENDER_NORMAL;
    // show the amount of the scene rendered. TODO this doesn't really work right with ghosting or shadow mapping on, as those also affect it.
    this.signalProgressByRendering = false;

    // How many ticks have executed the current command list. Good for knowing if we're on the first frame (tick 0).
    this.tickCount = 0;
    // average time spent rendering a tick() TODO - needs to be revisited and thought through: if a batch is not loaded, it displays really fast!
    this.beginFrameAvg = 0;
    // exactly what it says, the time stamp passed in on the previous tick().
    this.lastBeginFrameTimeStamp = 0;

    // type of rendering being done.
    this.renderType = RENDERMODE_FULL;

    // @see Viewer3DImpl.setProgressiveHaltFrame
    this.progressiveHaltFrame = 0;

    // First frame budget
    // If it's progressive and the first frame, try to finish the ground shadow in the allocated time
    this.INITIAL_GROUND_SHADOW = 0.2;

    // Internal command iterator state
    // is there a command list to execute?
    this.cmdListActive = false;
    // what command we are executing
    this.cmdIndex = 0;
    // are there "CMD_ALWAYS_DO" commands in the command list? If so, we need to traverse the whole command list each tick.
    this.encounteredAlwaysDo = false;
    // did the full render finish? If not, then ignore overlay and present updates until it has
    this.finishedFullRender = true;
    // did the ground shadow get computed in the post-process for deferred rendering?
    this.groundShadowInPost = false;
    // did any previous or current frame trigger the overlay to be drawn?
    this.drawOverlay = false;

    // true means parameters can be set on the command
    this.cmdIsOpen = false;

    // how long the array is (so that if new commands/params are needed, they get allocated first).
    this.allocArraySize = 0;
    // how many commands are in the active command list
    this.cmdListLength = 0;
    // the command and parameters set for the command
    this.cmdList = [];
    this.paramList = [];

    this.bindCommands();
  }


  bindCommands() {
    this.cmdBeginScene = cmdBeginScene.bind(this);
    this.cmdPostAndPresent = cmdPostAndPresent.bind(this);
    this.cmdBeginPhase = cmdBeginPhase.bind(this);
    this.cmdMainRender = cmdMainRender.bind(this);
    this.cmdRenderOverlays = cmdRenderOverlays.bind(this);
    this.cmdFinishAllRendering = cmdFinishAllRendering.bind(this);
    this.cmdForcePresent = cmdForcePresent.bind(this);
    this.cmdSuppressAO = cmdSuppressAO.bind(this);
    this.cmdRestoreAO = cmdRestoreAO.bind(this);
    this.cmdSceneAfterRender = cmdSceneAfterRender.bind(this);
    this.cmdGenerateGroundShadow = cmdGenerateGroundShadow.bind(this);
    this.cmdBlitGroundShadow = cmdBlitGroundShadow.bind(this);
    this.cmdSignalProcessingDone = cmdSignalProcessingDone.bind(this);
    this.cmdSignalRedraw = cmdSignalRedraw.bind(this);
    this.cmdUpdateShadowMap = cmdUpdateShadowMap.bind(this);
    this.cmdResetShadowMap = cmdResetShadowMap.bind(this);
    this.cmdFinishedFullRender = cmdFinishedFullRender.bind(this);
    this.cmdGenerateGroundReflection = cmdGenerateGroundReflection.bind(this);
  }

  isActive() {
    return this.cmdListActive;
  }

  /**
   * CPU wall-clock deadline for the current tick: the end of this viewer's allocated frame slice.
   * @returns {number}
   * @private
   */
  _computeFrameEndDeadline() {
    return performance.now() +
    RENDER_SOME_CPU_TIME_FRACTION * this.renderBudgetFraction * this.TARGET_FRAME_TIME;
  }

  /**
   * Remaining wall-clock time (ms) until this tick's frame-end deadline.
   * Falls back to the viewer's full target frame slice when no deadline is active yet.
   * @returns {number}
   */
  getRemainingFrameTime() {
    const now = performance.now();
    if (this._frameEndDeadline > now) {
      return this._frameEndDeadline - now;
    }
    if (this._frameEndDeadline !== Infinity) {
      return 0;
    }
    return RENDER_SOME_CPU_TIME_FRACTION * this.renderBudgetFraction * this.TARGET_FRAME_TIME;
  }

  /**
   * True when the active command set is a progressive main-render set, i.e. one headed by
   * cmdBeginScene. Frame-time samples are only taken from such sets, since cmdBeginScene is what
   * writes the baseline they are measured against.
   * Safe to call without checking cmdListActive; cmdListLength === 0 is the relevant guard.
   * @returns {boolean}
   * @private
   */
  _isProgressiveMainRenderSet() {
    // cmdList slots are reused across sets; without this, cmdList[0] can be stale.
    if (this.cmdListLength === 0) {
      return false;
    }
    // Overlay/present-only lists keep renderType === PROGRESSIVE but do not start with cmdBeginScene.
    if (this.cmdList[0] !== this.cmdBeginScene) {
      return false;
    }
    // Budget feedback applies to progressive rendering only (not full/silent passes).
    if (this.renderType !== RENDERMODE_PROGRESSIVE) {
      return false;
    }
    return true;
  }

  /**
   * Drops the frame-time baseline together with the consumption that is still waiting to be
   * reported against it. The two are only meaningful as a pair: without a baseline the pending
   * batches can no longer be attributed to the frame that produced them, and folding them into a
   * later frame's sample would charge two frames of work to a single frame time.
   * @private
   */
  _dropProgressiveFrameSample() {
    this.lastBeginFrameTimeStamp = 0;
    this._consumedRenderBudgetPending = 0;
  }

  /**
   * Wall-clock delta (ms) since the last cmdBeginScene, or undefined when not measurable on this
   * invoke. The baseline is single-use, so at most one sample is taken per cmdBeginScene: on the
   * first tick of a set it spans from the previous progressive frame, and on the continuation tick
   * right after it, it is the duration of that set's interactive first tick.
   * @returns {number|undefined}
   * @private
   */
  _getProgressiveFrameTimeDelta() {
    // lastBeginFrameTimeStamp is set by cmdBeginScene; the first pass has no baseline yet.
    if (!this._isProgressiveMainRenderSet() || this.lastBeginFrameTimeStamp <= 0) {
      return undefined;
    }
    return this.highResTimeStamp - this.lastBeginFrameTimeStamp;
  }

  /**
   * Updates targetRenderBudget from the previously measured frame's timing feedback.
   * Called at the start of executeCommandList whenever a frame-time sample is available.
   * @private
   */
  applyBudgetFeedback() {
    const delta = this._getProgressiveFrameTimeDelta();
    if (delta === undefined) {
      return;
    }

    const viewer = this.viewer;

    if (USE_OUT_OF_CORE_TILE_MANAGER) {
      renderBudgetManager.updateRenderBudget(
        viewer,
        viewer.modelQueue().isDone(),
        this._consumedRenderBudgetDelayed,
        delta,
        this.TARGET_FRAME_TIME * this.renderBudgetFraction,
        this.MAX_RENDER_BUDGET * this.renderBudgetFraction
      );
      // Hold this frame's sample back for the next feedback cycle: the delta measured then is
      // the one that reflects the work submitted now (~one frame of GPU lag). A tick that
      // rendered nothing (empty or already converged queue) is skipped rather than reported as
      // a consumption of zero: zero batches paired with a real frame time would corrupt the
      // controller's sensitivity estimate and its dead-time prediction, forcing a spurious
      // budget cut.
      if (this._consumedRenderBudgetPending > 0) {
        this._consumedRenderBudgetDelayed = this._consumedRenderBudgetPending;
        this._consumedRenderBudgetPending = 0;
      }
    } else {
      this.beginFrameAvg = 0.75 * this.beginFrameAvg + 0.25 * delta;
      this.updateTargetRenderBudget();
    }

    // Baseline is single-use: consumed for this feedback cycle; cmdBeginScene sets the next one.
    this._dropProgressiveFrameSample();
  }

  /**
   * @returns {number} Per-tick render budget for the active render mode (batches).
   * @private
   */
  resolveRenderBudget() {
    if (this.renderType === RENDERMODE_FULL) {
      return this.interruptBudget;
    }
    return this.targetRenderBudget;
  }

  // signal the beginning of a new set of commands
  beginCommandSet() {
    this.cmdListActive = true;
    this.cmdIndex = 0;
    this.cmdListLength = 0;
    this.encounteredAlwaysDo = false;
    this.tickCount = 0;
    this.screenDrawn = NOTHING_DRAWN;
    this.screenHasChanged = false;
  }

  // signal the end
  endCommandSet() {
    if (this.cmdIsOpen) {
      this.cmdIsOpen = false;
      // close previous command - really, increment just to get the final count
      this.cmdListLength++;
    }
  }

  addCommand(func, executionLevel) {
    if (this.cmdIsOpen) {
      // close previous command
      this.cmdListLength++;
    }
    this.cmdIsOpen = true;
    while (this.allocArraySize <= this.cmdListLength) {
      this.cmdList[this.cmdListLength] = {};
      this.paramList[this.cmdListLength] = {};
      this.allocArraySize++;
    }
    this.cmdList[this.cmdListLength] = func;
    this.paramList[this.cmdListLength].executionLevel = executionLevel || CMD_NORMAL_SEQUENCE;
    this.encounteredAlwaysDo = this.encounteredAlwaysDo || executionLevel === CMD_ALWAYS_DO;

    // return value so if we want to jump to this command, we know where to go.
    return this.cmdListLength;
  }

  // note that we're a bit sloppy with parameter setting. Since the parameter set at an index location
  // gets reused, you may see parameters in the parameter object that have nothing to do with this
  // command, since this parameter set might have been used for another command at some other time.
  // Basically, if a command doesn't use the parameter, then ignore it.
  setParam(indexString, val) {
    if (this.cmdIsOpen) {
      this.paramList[this.cmdListLength][indexString] = val;
    } else {
      if (ENABLE_DEBUG) {
        logger.error("ERROR: cannot set param when no command is open!");
      }
    }
  }

  // This method is meant for use during execution of a command, so gets the parameter from the currently-active command.
  getParam(indexString) {
    if (ENABLE_DEBUG) {
      if (this.paramList[this.cmdIndex][indexString] === undefined) {
        logger.error("ERROR: parameter " + indexString + " was never set for this command! Go fix it.");
      }
    }
    return this.paramList[this.cmdIndex][indexString];
  }

  // return true if done running all commands.
  executeCommandList() {

    if (!this.cmdListActive) {
      // Nothing to render: the frame that the pending sample belongs to is over and the next
      // cmdBeginScene starts a fresh measurement.
      this._dropProgressiveFrameSample();
      // Keep _consumedRenderBudgetDelayed: it is still owed to RenderBudgetManager on the next sample.

      // not active, so "done"
      return true;
    }

    // go through command list, interrupting as needed.

    if (this._isProgressiveMainRenderSet()) {
      this.applyBudgetFeedback();
    } else if (this.tickCount === 0) {
      // A new set that is not a progressive main render (overlay/present-only, full, silent, …).
      // Drop the sample so the next progressive pass does not measure this set as frame time.
      this._dropProgressiveFrameSample();
    }

    this.renderBudget = this.resolveRenderBudget();
    this.renderBudgetRemaining = this.renderBudget;
    if (USE_OUT_OF_CORE_TILE_MANAGER) {
      // A full render must run to completion in a single logical pass, matching the unbounded
      // batch budget resolveRenderBudget() returns for it. A finite CPU deadline would time-slice
      // it across ticks (and would also cap the ground shadow/reflection passes via
      // getRemainingFrameTime()). Progressive and silent ticks stay bounded by the frame-end deadline.
      this._frameEndDeadline = this.renderType === RENDERMODE_FULL ?
      Infinity :
      this._computeFrameEndDeadline();
    }

    if (ENABLE_DEBUG) {
      // reality check
      if (this.cmdIsOpen) {
        logger.error("ERROR: should call endCommandSet before executing");
      }
    }

    let restartIdx;
    // not at end of command list? We always go through the whole command list, as there may be "always do"
    // commands, such as a Present().

    if (ENABLE_DEBUG_RCS) {
      if (this.tickCount === 0) {console.log("===================");}
      console.log("Running commands for " + (this.renderType === RENDERMODE_PROGRESSIVE ? "progressive" :
      this.renderType === RENDERMODE_FULL ? "full" : "silent") +
      " render, for tick count " + this.tickCount);
    }

    // Are there any "always do" commands in this command set, that must be done before we continue our command sequence?
    // Currently needed by smooth navigation, to turn off AO during the render sequence.
    if (this.encounteredAlwaysDo) {
      let idx = 0;
      while (idx < this.cmdIndex) {
        // Is this a command we should always do?
        if (this.paramList[idx].executionLevel >= CMD_ALWAYS_DO) {
          // Commands we always do are assumed to never abort, so we don't check for failure.
          if (ENABLE_DEBUG_RCS) {console.log("  ALWAYS DO command " + idx + ": " + this.cmdList[idx].name);}
          this.cmdList[idx]();
        }
        idx++;
      }
    }

    let interrupted = false;
    while (this.cmdIndex < this.cmdListLength) {
      // "always do after" is for commands that should always execute, e.g., Present()
      const alwaysDoAfter = this.paramList[this.cmdIndex].executionLevel >= CMD_DO_AFTER;
      if (!interrupted || alwaysDoAfter) {
        ENABLE_DEBUG_RCS && console.log(`  command ${this.cmdIndex}: ${this.cmdList[this.cmdIndex].name} and ${this.renderBudgetRemaining} render budget remaining`);

        // true means stop executing, out of time (typically), so restart execution at this command the next tick()
        // signal to not execute any "normal sequence" commands for the rest of the command list.
        const requestInterrupt = Boolean(this.cmdList[this.cmdIndex]()); // can be true, false, and undefined
        interrupted ||= requestInterrupt; // note: once interrupted, we need to keep it truthy

        if (requestInterrupt) {
          const interruptBudgetLabel = this.renderBudgetRemaining;
          ENABLE_DEBUG_RCS && console.log(`  >>> out of tick time with ${interruptBudgetLabel}`);
          restartIdx = this.cmdIndex;
        }
      }
      // Go to next command until we hit the end of the list; we always continue, since there could be
      // "always do" or "do after" commands in the list that need to be executed.
      this.cmdIndex++;
    }

    // How much of our frame budget is already used up?
    // Note: this.renderBudgetRemaining can be negative
    this._consumedRenderBudget = this.renderBudget - this.renderBudgetRemaining;

    if (this.renderType === RENDERMODE_PROGRESSIVE) {
      if (this.tickCount === 0) {
        this._consumedRenderBudgetPending += this._consumedRenderBudget;
      }
    }

    if (interrupted) {
      this.cmdIndex = restartIdx; // set where to continue the work next tick()
    } else {
      this.cmdListActive = false; // executed all commands, so we're done
    }
    this.tickCount++;
    return interrupted;
  }

  /**
   * Sets MIN_RENDER_BUDGET / MAX_RENDER_BUDGET for the active render-budget mode.
   * OOC uses batch-count bounds from globals; legacy uses millisecond bounds (MAX is derived
   * from the default 15 FPS ceiling and updated again by setFPSTargets).
   * @private
   */
  _syncRenderBudgetBounds() {
    if (USE_OUT_OF_CORE_TILE_MANAGER) {
      this.MIN_RENDER_BUDGET = RENDER_BUDGET_PER_BATCH;
      this.MAX_RENDER_BUDGET = MAX_RENDER_BUDGET;
    } else {
      this.MIN_RENDER_BUDGET = MIN_BATCH_TIME_MS;
      this.MAX_RENDER_BUDGET = 1000 / 15;
    }
  }

  /**
   * Updates the per-tick target render budget.
   *
   * When using the OutOfCoreTileManager, new values are provided by RenderBudgetManager
   * and the budget is counted in batches. On the legacy path the budget is in milliseconds.
   *
   * @param {number} [newTargetRenderBudget] - New target render budget for this viewer
   * @param {number} [newRenderBudgetFraction] - Fraction of the shared render budget for this viewer
   * @private
   */
  updateTargetRenderBudget(newTargetRenderBudget, newRenderBudgetFraction) {
    if (USE_OUT_OF_CORE_TILE_MANAGER) {
      this.targetRenderBudget = newTargetRenderBudget ?? this.targetRenderBudget;
      this.renderBudgetFraction = newRenderBudgetFraction ?? this.renderBudgetFraction;
    } else {
      // Adjust frame time allowance based on actual frame rate, but stay within the given boundaries.
      if (this.beginFrameAvg < this.TARGET_FRAME_TIME && this.renderBudget < this.MAX_RENDER_BUDGET) {
        this.targetRenderBudget += 1;
      } else
      if (this.beginFrameAvg > this.TARGET_FRAME_TIME && this.renderBudget > this.MIN_RENDER_BUDGET) {
        this.targetRenderBudget *= 0.75 + 0.25 * this.TARGET_FRAME_TIME / this.beginFrameAvg;
      }
    }

    this.targetRenderBudget = this._clampTargetRenderBudget(this.targetRenderBudget);
  }

  /**
   * Clamps a render-budget value to the active mode's [MIN, MAX] bounds.
   * @param {number} value
   * @returns {number}
   * @private
   */
  _clampTargetRenderBudget(value) {
    return Math.min(Math.max(value, this.MIN_RENDER_BUDGET), this.MAX_RENDER_BUDGET);
  }

  /**
   * Sets the per-tick target render budget directly (in the active mode's native units:
   * batches with the OutOfCoreTileManager, milliseconds on the legacy path). Value is clamped.
   * @deprecated
   *
   * @param {number} targetRenderBudget
   */
  setTargetRenderBudget(targetRenderBudget) {
    this.targetRenderBudget = this._clampTargetRenderBudget(targetRenderBudget);
  }

  /**
   * Sets the per-tick target render budget from a desired frame time in milliseconds.
   * Preserves the legacy ms-based budget API (used by CrossFadeEffects). On the legacy path
   * the render budget is already in milliseconds, so the value is applied directly. With the
   * batch-based RenderBudgetManager, the frame time is converted to a batch budget using the
   * controller's most recent slope and frame-time measurement; if no measurement is available
   * yet, the current target render budget is kept.
   * @deprecated
   *
   * @param {number} frameBudgetMs - Desired render time in milliseconds.
   */
  setTargetFrameBudget(frameBudgetMs) {
    if (USE_OUT_OF_CORE_TILE_MANAGER) {
      const estimatedFullFrameBudget = renderBudgetManager.estimateRenderBudgetForFrameTime(frameBudgetMs);
      // Clamp the derived estimate: it can fall outside the valid range (e.g. negative when the
      // requested frame time is below the current one, or very large for a slack budget).
      this.targetRenderBudget = estimatedFullFrameBudget !== undefined ?
      this._clampTargetRenderBudget(estimatedFullFrameBudget * this.renderBudgetFraction) :
      this.targetRenderBudget;
    } else {
      // Legacy path: the render budget is already in milliseconds, so apply it directly.
      this.targetRenderBudget = frameBudgetMs;
    }
  }

  setFPSTargets(min, target, max) {
    this.TARGET_FRAME_TIME = 1000 / target;

    if (!USE_OUT_OF_CORE_TILE_MANAGER) {
      this.MAX_RENDER_BUDGET = 1000 / max;
      this.MIN_RENDER_BUDGET = MIN_BATCH_TIME_MS;
    }

    this.updateTargetRenderBudget();
  }

}